import api from './api';

export interface FtthCaixa {
  id: number;
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  port_id?: number; // Adicionado para compatibilidade com backend
  olt_id?: number; // Adicionado para compatibilidade com backend
  sfp?: string; // Adicionado para compatibilidade com backend
  capacidade: number | null; // Alterado: pode ser null para CEO
  portas_em_uso?: number; // Preenchido na listagem; usado para cálculo de portas disponíveis
  disabled_ports: Array<{
    port: number;
    reason: string;
    disabledAt: string;
    disabledBy: number;
  }>;
  status: 'ativo' | 'inativo' | 'aguardando_instalacao' | 'manutencao';
  monitor_status?: 'online' | 'offline' | 'vazia';
  monitor_ultima_verificacao_em?: string | null;
  monitor_ultimo_online_em?: string | null;
  monitor_ultimo_offline_em?: string | null;
  funcionamento?: 'online' | 'offline' | 'vazia';
  tipo: 'cto' | 'ceo' | 'reserva';
  tipo_rede?: 'propria' | 'neutra';
  operadora?: string;
  color?: string | null;
  icon?: string | null;
  color_text?: string | null;
  escritorio_id: number; // Obrigatório - escritório responsável pela caixa
  pontos_id?: number[]; // IDs dos pontos de cabos vinculados a esta caixa
  cabo_id?: number | null;
  metragem?: number | null;
  cabo?: {
    id: number;
    titulo?: string;
  };
  usuarioId_criou: number;
  createdAt: string;
  updatedAt: string;
  usuario_criou?: {
    id: number;
    nome: string;
    usuario: string;
  };
  pon_port?: {
    id: number;
    portNumber?: string;
    port_number?: string;
    sfp?: string;
    slot?: number;
    olt?: {
      id: number;
      name: string;
      vendor: string;
      ip_address: string;
    };
  };
}

export interface FtthCaixaStats {
  summary: {
    total: number;
    ativo: number;
    inativo: number;
    aguardando_instalacao: number;
    manutencao: number;
  };
  byPorts: Array<{
    capacidade: number;
    total: number;
  }>;
  withCoordinates: number;
  linkedToPorts: number;
  unlinked: number;
}

export interface CtoPortClientInfo {
  client_id?: number | null;
  client_name?: string | null;
  contract_id?: number | null;
  contract_title?: string | null;
  contrato_servico_id?: number | null;
  service_status?: string | null;
  service_type?: string | null;
  equipment_id?: number;
  numero_serie?: string;
  circuito_id?: number | null;
  circuito_identificacao?: string | number | null;
  modelo_tipo?: string | null;
  rx_power?: number | null;
  reason?: string;
  date?: string | null;
}

export interface CtoPortWithClient {
  port_number: number;
  status: 'available' | 'occupied' | 'disabled';
  info: CtoPortClientInfo | null;
}

export interface GetClientsByCtoData {
  caixa_id: number;
  caixa_name: string;
  capacidade: number;
  tipo: 'cto' | 'ceo' | 'reserva';
  status: string;
  occupied_ports_count: number;
  available_ports_count: number;
  disabled_ports_count: number;
  ports: CtoPortWithClient[];
}

export interface GetClientsByCtoResponse {
  success: boolean;
  data?: GetClientsByCtoData;
  message?: string;
}

class FtthCaixaService {
  // Listar CAIXAs com filtros (nova implementação padronizada)
  async getCaixas(params?: {
    page?: number;
    limit?: number;
    status?: string;
    monitor_status?: string;
    pon_port_id?: number;
    olt_id?: number;
    sfp?: string;
    capacidade?: number;
    search?: string;
    tipo?: string;
    tipos?: string;
    escritorio_id?: number;
  }) {
    const response = await api.get('/ftth-caixas', { params });
    return response.data;
  }

  // Listar CAIXAs CTO vinculadas a uma porta PON
  async getCaixasByPortId(portId: number): Promise<FtthCaixa[]> {
    const response = await api.get('/ftth-caixas', {
      params: { port_id: portId, tipo: 'cto', limit: 500 }
    });
    const data = response.data?.data ?? response.data;
    const caixas = data?.caixas ?? data;
    return Array.isArray(caixas) ? caixas : [];
  }

  // Listar CAIXAs CTO por OLT e SFP
  async getCaixasByOltSfp(oltId: number, sfp: string): Promise<FtthCaixa[]> {
    const response = await api.get('/ftth-caixas', {
      params: { olt_id: oltId, sfp, tipo: 'cto', limit: 500 }
    });
    const data = response.data?.data ?? response.data;
    const caixas = data?.caixas ?? data;
    return Array.isArray(caixas) ? caixas : [];
  }

  // Listar CAIXAs (método legado - manter por compatibilidade)
  async getAll(params?: {
    page?: number;
    limit?: number;
    status?: string;
    monitor_status?: string;
    pon_port_id?: number;
    olt_id?: number;
    sfp?: string;
    capacidade?: number;
    search?: string;
    tipo?: string;
    tipos?: string;
    escritorio_id?: number;
  }) {
    const response = await api.get('/ftth-caixas', { params });
    return response.data;
  }

  // Obter estatísticas
  async getStats(): Promise<FtthCaixaStats> {
    const response = await api.get('/ftth-caixas/stats');
    const backendData = response.data; // Dados estão diretamente em response.data

    if (!backendData || !backendData.summary) {
      console.error('❌ Dados CAIXAs inválidos:', backendData);
      throw new Error('Estrutura de dados CAIXAs inválida');
    }

    return backendData;
  }

  // Obter CAIXA específica
  async getById(id: number): Promise<{ success: boolean; data: FtthCaixa }> {
    const response = await api.get(`/ftth-caixas/${id}`);

    // Se response.data já tem a estrutura {success, data}, retorna como está
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      return response.data;
    }

    // Se response.data é diretamente a CAIXA, wrapper com success
    return {
      success: true,
      data: response.data
    };
  }

  // Criar CAIXA
  async create(data: Partial<FtthCaixa>): Promise<{ success: boolean; data: FtthCaixa }> {
    const response = await api.post('/ftth-caixas', data);
    return response.data;
  }

  // Atualizar CAIXA
  async update(id: number, data: Partial<FtthCaixa>): Promise<{ success: boolean; data: FtthCaixa }> {
    const response = await api.put(`/ftth-caixas/${id}`, data);
    return response.data;
  }

  // Excluir CAIXA
  async delete(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/ftth-caixas/${id}`);
    return response.data;
  }



  // Alterar status da porta (com fallback para APIs existentes)
  async changePort(id: number, port: number, action: 'enable' | 'disable', reason?: string): Promise<{ success: boolean; data: FtthCaixa }> {
    try {
      // Tentar usar a nova API unificada primeiro
      const payload: any = { port, action };
      if (action === 'disable' && reason) {
        payload.reason = reason;
      }
      const response = await api.post(`/ftth-caixas/${id}/changeport`, payload);
      return response.data;
    } catch (error: any) {
      // Se a API unificada não existir (404), usar APIs separadas
      if (error.response?.status === 404) {
        console.log('🔄 API changeport não encontrada, usando fallback para APIs separadas');

        if (action === 'enable') {
          return this.enablePortDirect(id, port);
        } else {
          return this.disablePortDirect(id, port, reason!);
        }
      }
      // Re-throw outros erros
      throw error;
    }
  }

  // Funções diretas para fallback
  private async enablePortDirect(id: number, port: number): Promise<{ success: boolean; data: FtthCaixa }> {
    const response = await api.post(`/ftth-caixas/${id}/ports/enable`, { port });
    return response.data;
  }

  private async disablePortDirect(id: number, port: number, reason: string): Promise<{ success: boolean; data: FtthCaixa }> {
    const response = await api.post(`/ftth-caixas/${id}/ports/disable`, { port, reason });
    return response.data;
  }

  // Desabilitar porta (mantido para compatibilidade)
  async disablePort(id: number, port: number, reason: string): Promise<{ success: boolean; data: FtthCaixa }> {
    return this.changePort(id, port, 'disable', reason);
  }

  // Habilitar porta (mantido para compatibilidade)
  async enablePort(id: number, port: number): Promise<{ success: boolean; data: FtthCaixa }> {
    return this.changePort(id, port, 'enable');
  }

  // Buscar CAIXAs próximas
  async getNearCaixas(latitude: number, longitude: number, radius: number = 1): Promise<{ success: boolean; data: FtthCaixa[] }> {
    const response = await api.get('/ftth-caixas/near', {
      params: { latitude, longitude, radius }
    });
    return response.data;
  }

  // Portas da CTO com clientes/contratos vinculados
  async getClientsByCto(caixaId: number): Promise<GetClientsByCtoResponse> {
    const response = await api.get(`/ftth-caixas/${caixaId}/get_clients_by_cto`);
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      return response.data;
    }
    return {
      success: true,
      data: response.data
    };
  }


}

export const ftthCaixaService = new FtthCaixaService(); 